// src/hooks/useSeatSelection.js
//
// All the buyer-side state machine lives here so the canvas component stays dumb.
//
//  * server is the source of truth for seat state; every click is an optimistic
//    update that is rolled back if the hold mutation says someone else won
//  * one hold token per buyer + chart event (kept in sessionStorage, or taken from
//    the cart item when coming back from the cart) -> one countdown, one cart token
//  * realtime: Centrifugo pushes patch the status map; a visibility/interval
//    refetch reconciles anything a dropped websocket missed
//  * ticket types are assigned to seats by seatAssignment.js; the budget (tickets
//    per type) can grow when an extra single-type seat is picked

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";

import { useMutationWithAuth, useQueryWithAuth } from "src/graphql/instances";
import useChartEvent from "src/hooks/useChartEvent";
import { useCentrifugo } from "src/services/WebsocketsProvider";
import { GET_CHART_EVENT_SEAT_STATES } from "src/graphql/queries/seatingChartSales";
import { HOLD_SEATS_FOR_SALE, RELEASE_SEATS_FOR_SALE } from "src/graphql/mutations/seatingChartSales";
import {
  applyIncrease,
  assignTicketTypes,
  buildEligibility,
  groupIntoCartItems,
  planSeatAdd,
  planSeatRemove,
  sumBudget,
  buildInventoryRows,
} from "../utils/seatingChartAssignment";
import { suggestSeats as suggestSeatsFor } from "../utils/seatingChartSuggestion";
import { useLocales } from "src/locales";


const GRACE_MS = 4000; // ignore "lost" signals for seats we confirmed this recently (stale echoes)
const SAFETY_REFETCH_MS = 45_000;
const MAX_SUGGEST_ATTEMPTS = 3;

const storageGet = (k) => {
  try {
    return window.sessionStorage.getItem(k);
  } catch (e) {
    return null;
  }
};
const storageSet = (k, v) => {
  try {
    if (v) window.sessionStorage.setItem(k, v);
    else window.sessionStorage.removeItem(k);
  } catch (e) {
    /* private mode etc. - the token just won't survive a refresh */
  }
};

const normalize = (b) => Object.fromEntries(Object.entries(b).filter(([, q]) => q > 0));
const budgetFromQuantities = (quantities) =>
  normalize(Object.fromEntries((quantities ?? []).map((q) => [q.id, q.quantity])));
const sameBudget = (a, b) => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
};

/**
 * Turn a budget back into the quantity list your three screens keep in state,
 * preserving whatever extra fields the existing entries carry.
 * Usage:  onBudgetChange={(b) => setLocalQuantity((prev) => budgetToQuantities(b, prev, ticketTypes))}
 */
export const budgetToQuantities = (budget, previous, ticketTypes, extra = {}) => {
  const prevById = Object.fromEntries((previous ?? []).map((q) => [q.id, q]));
  const typesById = Object.fromEntries((ticketTypes ?? []).map((t) => [t.id, t]));
  return Object.entries(normalize(budget)).map(([id, quantity]) => {
    const prev = prevById[id];
    if (prev) return { ...prev, quantity };
    const tt = typesById[id] ?? {};
    return { id, name: tt.name, price: tt.price, quantity, seatIds: [], seatsHoldToken: undefined, ...extra };
  });
};

export default function useSeatSelection({
  chartEventId,
  chart,
  ticketTypes,
  quantities,
  onBudgetChange,
  initialHoldToken,
  holdTtlMinutes = 15,
  maxTotalTickets = Infinity,
  otherCartTickets = 0,
  autoIncreaseWhen = "always",
  autoSuggest = true,
}) {
  const seats = chart?.seats;
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const notify = useCallback(
    (code) =>
      enqueueSnackbar(translate(`seatingChart.${code}`), {
        variant: code === "SUGGESTED" ? "info" : "warning",
        autoHideDuration: 4000,
      }),
    [enqueueSnackbar],
  );

  // callers usually pass a fresh array every render (sortTickets([...]) etc.) - key it by content
  const ticketTypesKey = JSON.stringify(
    (ticketTypes ?? []).map((t) => [t.id, t.isPublished, t.available, (t.seatingChartCategories ?? []).map((c) => c.id)]),
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTicketTypes = useMemo(() => ticketTypes ?? [], [ticketTypesKey]);
  const eligibility = useMemo(() => buildEligibility(stableTicketTypes), [stableTicketTypes]);
  const ticketTypesById = useMemo(
    () => Object.fromEntries(stableTicketTypes.map((t) => [t.id, t])),
    [stableTicketTypes],
  );
  const seatsById = useMemo(() => new Map((seats ?? []).map((s) => [s.id, s])), [seats]);

  // ---- budget (tickets per type) ---------------------------------------------
  const [budget, setBudgetState] = useState(() => budgetFromQuantities(quantities));
  const budgetRef = useRef(budget);
  const setBudget = useCallback(
    (b) => {
      const n = normalize(b);
      budgetRef.current = n;
      setBudgetState(n);
      onBudgetChange?.(n);
    },
    [onBudgetChange],
  );
  useEffect(() => {
    const fromProps = budgetFromQuantities(quantities);
    if (!sameBudget(fromProps, budgetRef.current)) {
      budgetRef.current = fromProps;
      setBudgetState(fromProps);
    }
  }, [quantities]);

  // ---- selection ---------------------------------------------------------------
  const [selected, setSelectedState] = useState([]); // [{seatId, categoryId, at}]
  const selectedRef = useRef([]);
  const setSelected = useCallback((next) => {
    selectedRef.current = next;
    setSelectedState(next);
  }, []);
  const [assignment, setAssignment] = useState({});
  const autoAddedRef = useRef({});
  const [pending, setPending] = useState(() => new Set());
  const pendingRef = useRef(pending);
  const markPending = useCallback((id, on) => {
    const next = new Set(pendingRef.current);
    if (on) next.add(id);
    else next.delete(id);
    pendingRef.current = next;
    setPending(next);
  }, []);
  const releasingRef = useRef(new Set());
  // Seats that belong to the cart (restored on arrival) and that the buyer un-selected here. They stay HELD on the
  // server until the cart is updated, otherwise validateCart() would find a cart item pointing at a released seat
  // and delete it. Released by releaseDeferred() once the cart operation succeeded.
  const deferredRef = useRef(new Set());
  const [deferredIds, setDeferredIds] = useState(() => new Set());
  const committedRef = useRef(false); // true once the cart owns the holds
  const [chooser, setChooser] = useState(null); // { seat, options } when several ticket types fit

  // ---- hold token + countdown ---------------------------------------------------
  const storageKey = `nightz:seatHold:${chartEventId}`;
  const holdTokenRef = useRef(initialHoldToken ?? storageGet(storageKey) ?? null);
  const adoptToken = useCallback(
    (t) => {
      holdTokenRef.current = t;
      storageSet(storageKey, t);
    },
    [storageKey],
  );
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [expiresAt]);
  const secondsLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : null;

  // ---- server state ---------------------------------------------------------------
  const { data, refetch } = useQueryWithAuth(GET_CHART_EVENT_SEAT_STATES, {
    variables: { chartEventId, holdToken: holdTokenRef.current },
    fetchPolicy: "network-only",
    skip: !chartEventId,
  });
  const [statusById, setStatusById] = useState({});
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const statusRef = useRef(statusById);
  statusRef.current = statusById;
  const [hold] = useMutationWithAuth(HOLD_SEATS_FOR_SALE);
  const [release] = useMutationWithAuth(RELEASE_SEATS_FOR_SALE);
  const releaseRef = useRef(release);
  releaseRef.current = release;

  const releaseOnServer = useCallback(
    (seatIds) => {
      if (!seatIds.length || !holdTokenRef.current) return Promise.resolve();
      return releaseRef
        .current({ variables: { input: { chartEventId, seatIds, holdToken: holdTokenRef.current } } })
        .catch((e) => console.error("releaseSeatsForSale failed", e));
    },
    [chartEventId],
  );

  const dropSeats = useCallback(
    (ids, code) => {
      const remaining = selectedRef.current.filter((s) => !ids.includes(s.seatId));
      const plan = planSeatRemove({ remaining, budget: budgetRef.current, autoAdded: autoAddedRef.current, eligibility });
      autoAddedRef.current = plan.autoAdded;
      if (plan.budget !== budgetRef.current) setBudget(plan.budget);
      setSelected(remaining);
      setAssignment(plan.assignment);
      if (!remaining.length) setExpiresAt(null);
      if (code) notify(code);
    },
    [eligibility, notify, setBudget, setSelected],
  );

  /**
   * Make local selection agree with the server's view of "seats held by my token".
   * Used for: first load (restore after refresh / coming back from the cart),
   * the safety refetch, and tab-visible refetch.
   */
  const reconcile = useCallback(
    (states) => {
      const mineIds = new Set(states.filter((r) => r.mine).map((r) => r.seatId));

      const stale = selectedRef.current.filter(
        (s) => !mineIds.has(s.seatId) && !pendingRef.current.has(s.seatId) && Date.now() - s.at > GRACE_MS,
      );
      if (stale.length) dropSeats(stale.map((s) => s.seatId), "SEAT_LOST");

      const have = new Set(selectedRef.current.map((s) => s.seatId));
      const missing = states.filter(
        (r) =>
          r.mine &&
          !have.has(r.seatId) &&
          !pendingRef.current.has(r.seatId) &&
          !releasingRef.current.has(r.seatId) &&
          !deferredRef.current.has(r.seatId),
      );
      if (!missing.length) return;

      let sel = [...selectedRef.current];
      const overflow = [];
      let latestExpiry = null;
      for (const r of missing) {
        const seat = seatsById.get(r.seatId);
        if (!seat) continue;
        // restored = already held by this token when we arrived (cart item / page refresh). Those are not
        // auto-released when the screen closes, because the cart may be depending on them.
        const cand = { seatId: r.seatId, categoryId: seat.categoryId, at: 0, restored: true };
        const res = assignTicketTypes([...sel, cand], budgetRef.current, eligibility);
        if (res.ok) {
          sel = [...sel, cand];
          if (r.holdExpiresAt && (!latestExpiry || new Date(r.holdExpiresAt) > latestExpiry)) {
            latestExpiry = new Date(r.holdExpiresAt);
          }
        } else {
          overflow.push(r.seatId); // held by us but doesn't fit the tickets chosen -> give it back
        }
      }
      if (sel.length !== selectedRef.current.length) {
        setSelected(sel);
        setAssignment(assignTicketTypes(sel, budgetRef.current, eligibility).assignment);
        if (latestExpiry) setExpiresAt(latestExpiry.getTime());
      }
      if (overflow.length) releaseOnServer(overflow);
    },
    [dropSeats, eligibility, releaseOnServer, seatsById, setSelected],
  );

  // first load + every refetch result (reconcile via ref so a new callback identity can't re-run this
  // effect and overwrite fresher realtime patches with stale query data)
  const reconcileRef = useRef(reconcile);
  reconcileRef.current = reconcile;
  useEffect(() => {
    const rows = data?.chartEventSeatStates;
    if (!rows || !seatsById.size) return;
    const map = {};
    rows.forEach((r) => {
      map[r.seatId] = r.status;
    });
    setStatusById(map);
    setStatusesLoaded(true);
    reconcileRef.current(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, seatsById.size]);

  // realtime
  useChartEvent(chartEventId, (msg) => {
    if (msg?.type !== "SEAT_STATUS_CHANGED") return;
    setStatusById((prev) => {
      const next = { ...prev };
      msg.seatIds.forEach((id) => {
        next[id] = msg.status;
      });
      return next;
    });
    if (msg.status !== "HELD") {
      const lost = selectedRef.current.filter(
        (s) => msg.seatIds.includes(s.seatId) && !pendingRef.current.has(s.seatId) && Date.now() - s.at > GRACE_MS,
      );
      if (lost.length) dropSeats(lost.map((s) => s.seatId), "SEAT_LOST");
    }
  });

  // safety net for dropped websockets / sleeping tabs
  useEffect(() => {
    if (!chartEventId) return undefined;
    const doRefetch = () => refetch({ chartEventId, holdToken: holdTokenRef.current });
    const onVisible = () => document.visibilityState === "visible" && doRefetch();
    document.addEventListener("visibilitychange", onVisible);
    const i = setInterval(doRefetch, SAFETY_REFETCH_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(i);
    };
  }, [chartEventId, refetch]);

  // websocket (re)connected -> we may have missed pushes while it was down, resync from the server
  const { centrifugoConnected } = useCentrifugo();
  useEffect(() => {
    if (centrifugoConnected && chartEventId) refetch({ chartEventId, holdToken: holdTokenRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centrifugoConnected]);

  // countdown reached zero
  useEffect(() => {
    if (secondsLeft === 0 && selectedRef.current.length && !committedRef.current) {
      dropSeats(selectedRef.current.map((s) => s.seatId), "HOLD_EXPIRED");
      refetch({ chartEventId, holdToken: holdTokenRef.current });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  // ---- actions --------------------------------------------------------------------
  const commitAdd = useCallback(
    async (cand, plan, opts = {}) => {
      const prevBudget = budgetRef.current;
      const prevAuto = autoAddedRef.current;
      if (plan.kind === "increase") {
        autoAddedRef.current = { ...prevAuto, [plan.ticketTypeId]: (prevAuto[plan.ticketTypeId] ?? 0) + 1 };
        setBudget(plan.budget);
      }
      setSelected([...selectedRef.current, cand]);
      setAssignment(plan.assignment);
      if (opts.skipHold) {
        // re-selecting a cart seat we never released: it is still held under our token, no server call needed
        deferredRef.current.delete(cand.seatId);
        setDeferredIds(new Set(deferredRef.current));
        return;
      }
      markPending(cand.seatId, true);

      const rollback = (status) => {
        const remaining = selectedRef.current.filter((s) => s.seatId !== cand.seatId);
        setSelected(remaining);
        setAssignment(assignTicketTypes(remaining, prevBudget, eligibility).assignment);
        autoAddedRef.current = prevAuto;
        if (plan.kind === "increase") setBudget(prevBudget);
        if (status) setStatusById((p) => ({ ...p, [cand.seatId]: status }));
      };

      try {
        const res = await hold({
          variables: { input: { chartEventId, seatIds: [cand.seatId], holdToken: holdTokenRef.current } },
        });
        const out = res.data.holdSeatsForSale;
        const mine = out.seats[0];
        if (!mine?.holdToken) {
          rollback(mine?.status ?? "HELD");
          notify("SEAT_TAKEN");
          return;
        }
        adoptToken(out.holdToken);
        // server slides the expiry of every seat on the token to now + ttl
        setExpiresAt(Date.now() + holdTtlMinutes * 60_000);
        setSelected(selectedRef.current.map((s) => (s.seatId === cand.seatId ? { ...s, at: Date.now() } : s)));
        setStatusById((p) => ({ ...p, [cand.seatId]: "HELD" }));
      } catch (e) {
        // a thrown error is NOT a lost race (a lost race comes back as holdToken: null) - don't say "someone took it"
        console.error("holdSeatsForSale failed", e?.graphQLErrors?.[0]?.message ?? e);
        rollback();
        notify("GENERIC");
      } finally {
        markPending(cand.seatId, false);
      }
    },
    [adoptToken, chartEventId, eligibility, hold, holdTtlMinutes, markPending, notify, setBudget, setSelected],
  );

  const chooseTicketType = useCallback(
    async (ticketTypeId) => {
      if (!chooser) return;
      const { seat } = chooser;
      setChooser(null);
      const r = applyIncrease({
        selected: selectedRef.current,
        seat,
        budget: budgetRef.current,
        eligibility,
        ticketTypeId,
      });
      if (!r.ok) {
        notify("NO_TICKET_TYPE");
        return;
      }
      await commitAdd(
        { ...seat, at: Date.now() },
        { kind: "increase", ticketTypeId, budget: r.budget, assignment: r.assignment },
        { skipHold: deferredRef.current.has(seat.seatId) },
      );
    },
    [chooser, commitAdd, eligibility, notify],
  );

  /**
   * Inventory delete icon.
   *   seated row     -> un-select the seat AND remove its ticket (nothing is left without a seat)
   *   unassigned row -> just remove that ticket from the order
   * (Clicking a selected seat on the map is different: it only removes a ticket the buyer added by clicking;
   *  a ticket that came from the previous step goes back to "no seat selected" - see planSeatRemove.)
   */
  const removeTicket = useCallback(
    (row) => {
      const t = row.ticketTypeId;
      const newBudget = { ...budgetRef.current, [t]: Math.max(0, (budgetRef.current[t] ?? 0) - 1) };
      const auto = { ...autoAddedRef.current };
      if ((auto[t] ?? 0) > newBudget[t]) auto[t] = newBudget[t];

      if (row.kind !== "seated") {
        autoAddedRef.current = auto;
        setBudget(newBudget);
        return;
      }

      const existing = selectedRef.current.find((s) => s.seatId === row.seatId);
      if (!existing || pendingRef.current.has(row.seatId)) return;
      const remaining = selectedRef.current.filter((s) => s.seatId !== row.seatId);
      const m = assignTicketTypes(remaining, newBudget, eligibility);
      if (!m.ok) return;

      if (existing.restored) {
        // cart-owned seat: keep the hold until the cart is updated (see deferredRef)
        deferredRef.current.add(existing.seatId);
        setDeferredIds(new Set(deferredRef.current));
      } else {
        releasingRef.current.add(existing.seatId);
        setStatusById((p) => ({ ...p, [existing.seatId]: "AVAILABLE" }));
        releaseOnServer([existing.seatId]).finally(() => {
          setTimeout(() => releasingRef.current.delete(existing.seatId), 2000);
        });
      }
      autoAddedRef.current = auto;
      setBudget(newBudget);
      setSelected(remaining);
      setAssignment(m.assignment);
      if (!remaining.length) setExpiresAt(null);
    },
    [eligibility, releaseOnServer, setBudget, setSelected],
  );

  const deselect = useCallback(
    (existing) => {
      const ticketTypeId = assignTicketTypes(selectedRef.current, budgetRef.current, eligibility).assignment[existing.seatId];
      if (ticketTypeId) {
        removeTicket({ kind: "seated", seatId: existing.seatId, ticketTypeId });
        return;
      }
      // no ticket type found for the seat (should not happen): fall back to just un-selecting it
      if (existing.restored) {
        deferredRef.current.add(existing.seatId);
        setDeferredIds(new Set(deferredRef.current));
        dropSeats([existing.seatId]);
        return;
      }
      releasingRef.current.add(existing.seatId);
      dropSeats([existing.seatId]);
      setStatusById((p) => ({ ...p, [existing.seatId]: "AVAILABLE" }));
      releaseOnServer([existing.seatId]).finally(() => {
        setTimeout(() => releasingRef.current.delete(existing.seatId), 2000);
      });
    },
    [dropSeats, eligibility, releaseOnServer, removeTicket],
  );

  const toggleSeat = useCallback(
    async (seatId) => {
      if (pendingRef.current.has(seatId)) return;
      const existing = selectedRef.current.find((s) => s.seatId === seatId);
      if (existing) {
        deselect(existing);
        return;
      }
      const seat = seatsById.get(seatId);
      if (!seat) return;
      const isDeferred = deferredRef.current.has(seatId);
      const currentStatus = isDeferred ? "AVAILABLE" : statusRef.current[seatId] ?? "AVAILABLE";
      if (currentStatus !== "AVAILABLE") {
        notify(currentStatus === "BOOKED" ? "SEAT_BOOKED" : currentStatus === "HELD" ? "SEAT_HELD" : "SEAT_UNAVAILABLE");
        return;
      }
      const cand = { seatId, categoryId: seat.categoryId, at: Date.now(), ...(isDeferred && { restored: true }) };
      const plan = planSeatAdd({
        selected: selectedRef.current,
        seat: cand,
        budget: budgetRef.current,
        eligibility,
        ticketTypesById,
        autoIncreaseWhen,
        maxTotalTickets,
        otherCartTickets,
      });
      if (plan.kind === "blocked") {
        notify(plan.reason);
        return;
      }
      if (plan.kind === "choose") {
        setChooser({ seat: cand, options: plan.options });
        return;
      }
      await commitAdd(cand, plan, { skipHold: isDeferred });
    },
    [autoIncreaseWhen, commitAdd, deselect, eligibility, maxTotalTickets, notify, otherCartTickets, seatsById, ticketTypesById],
  );

  /** Several seats in one hold request (used by the suggestion). Optimistic, rolls back per seat. */
  const commitSeatsBatch = useCallback(
    async (cands, { silent = false } = {}) => {
      const next = [...selectedRef.current, ...cands];
      const m = assignTicketTypes(next, budgetRef.current, eligibility);
      if (!m.ok) return { failed: [], error: true };
      setSelected(next);
      setAssignment(m.assignment);
      cands.forEach((c) => markPending(c.seatId, true));

      const dropFailed = (failedIds) => {
        const remaining = selectedRef.current.filter((s) => !failedIds.has(s.seatId));
        setSelected(remaining);
        setAssignment(assignTicketTypes(remaining, budgetRef.current, eligibility).assignment);
      };

      try {
        const res = await hold({
          variables: { input: { chartEventId, seatIds: cands.map((c) => c.seatId), holdToken: holdTokenRef.current } },
        });
        const out = res.data.holdSeatsForSale;
        const heldIds = new Set(out.seats.filter((x) => x.holdToken).map((x) => x.seatId));
        if (heldIds.size) {
          adoptToken(out.holdToken);
          setExpiresAt(Date.now() + holdTtlMinutes * 60_000);
        }
        const failed = new Set(cands.filter((c) => !heldIds.has(c.seatId)).map((c) => c.seatId));
        const confirmedAt = Date.now();
        setSelected(
          selectedRef.current
            .filter((s) => !failed.has(s.seatId))
            .map((s) => (heldIds.has(s.seatId) ? { ...s, at: confirmedAt } : s)),
        );
        setAssignment(assignTicketTypes(selectedRef.current, budgetRef.current, eligibility).assignment);
        setStatusById((p) => {
          const n = { ...p };
          out.seats.forEach((x) => {
            n[x.seatId] = x.holdToken ? "HELD" : x.status;
          });
          return n;
        });
        if (failed.size && !silent) notify("SEAT_TAKEN");
        return { failed: [...failed], error: false };
      } catch (e) {
        console.log("holdSeatsForSale (batch) failed", e?.graphQLErrors?.[0]?.message ?? e);
        dropFailed(new Set(cands.map((c) => c.seatId)));
        notify("GENERIC");
        return { failed: [], error: true };
      } finally {
        cands.forEach((c) => markPending(c.seatId, false));
      }
    },
    [adoptToken, chartEventId, eligibility, hold, holdTtlMinutes, markPending, notify, setSelected],
  );

  const suggestingRef = useRef(false);
  /** "Best available" for every ticket that has no seat yet. */
  const suggestSeatsNow = useCallback(
    async ({ announce = false } = {}) => {
      if (suggestingRef.current || !chart?.seats?.length) return;
      suggestingRef.current = true;
      try {
        const avoid = new Set(); // seats lost to someone else during this run
        const runHeld = new Set(); // seats this run already holds

        for (let attempt = 0; attempt < MAX_SUGGEST_ATTEMPTS; attempt += 1) {
          // everything the buyer has that is NOT from this run
          const base = selectedRef.current.filter((s) => !runHeld.has(s.seatId));
          const count = sumBudget(budgetRef.current) - base.length;
          if (count <= 0) return;

          const taken = new Set(base.map((s) => s.seatId));
          const isFree = (seat) =>
            !avoid.has(seat.id) &&
            !taken.has(seat.id) &&
            !deferredRef.current.has(seat.id) &&
            (runHeld.has(seat.id) ||
              (!pendingRef.current.has(seat.id) && (statusRef.current[seat.id] ?? "AVAILABLE") === "AVAILABLE")) &&
            (eligibility.get(seat.categoryId) ?? []).some((t) => (ticketTypesById[t]?.available ?? 1) > 0);

          const ids = suggestSeatsFor({
            chart,
            isFree,
            selected: base,
            budget: budgetRef.current,
            eligibility,
            count,
            keep: runHeld,
          });
          if (!ids.length) {
            // nothing left to offer: stay quiet on the automatic first run, otherwise say so
            if (!announce || attempt > 0) notify("NO_SUGGESTION");
            return;
          }

          // give back what the new plan doesn't use, then hold what is new
          const toRelease = [...runHeld].filter((id) => !ids.includes(id));
          const toHold = ids.filter((id) => !runHeld.has(id));

          if (toRelease.length) {
            const remaining = selectedRef.current.filter((s) => !toRelease.includes(s.seatId));
            setSelected(remaining);
            setAssignment(assignTicketTypes(remaining, budgetRef.current, eligibility).assignment);
            toRelease.forEach((id) => {
              runHeld.delete(id);
              releasingRef.current.add(id);
            });
            setStatusById((p) => {
              const n = { ...p };
              toRelease.forEach((id) => {
                n[id] = "AVAILABLE";
              });
              return n;
            });
            await releaseOnServer(toRelease);
            setTimeout(() => toRelease.forEach((id) => releasingRef.current.delete(id)), 2000);
          }
          if (!toHold.length) return; // the plan is what we already hold

          const at = Date.now();
          const { failed, error } = await commitSeatsBatch(
            toHold.map((id) => ({ seatId: id, categoryId: seatsById.get(id).categoryId, at })),
            { silent: true },
          );
          if (error) return; // real error, already reported
          toHold.filter((id) => !failed.includes(id)).forEach((id) => runHeld.add(id));
          if (!failed.length) return; // everything held
          failed.forEach((id) => avoid.add(id));
        }

        // every attempt lost at least one seat to somebody else
        if (sumBudget(budgetRef.current) - selectedRef.current.length > 0) notify("SEAT_TAKEN");
      } finally {
        suggestingRef.current = false;
      }
    },
    [chart, commitSeatsBatch, eligibility, notify, releaseOnServer, seatsById, ticketTypesById],
  );


  // On arrival: if some tickets have no seat yet, suggest seats once (the buyer can undo it in the panel)
  const suggestRef = useRef(suggestSeatsNow);
  suggestRef.current = suggestSeatsNow;
  const autoSuggestedRef = useRef(false);
  const [suggestSettled, setSuggestSettled] = useState(!autoSuggest);

  useEffect(() => {
    if (!autoSuggest || autoSuggestedRef.current || !statusesLoaded || !chart?.seats?.length) return;
    autoSuggestedRef.current = true;
    Promise.resolve(suggestRef.current({ announce: true })).finally(() => setSuggestSettled(true));
  }, [autoSuggest, statusesLoaded, chart]);

  // safety net: never leave the panel hidden if something goes wrong
  useEffect(() => {
    const t = setTimeout(() => setSuggestSettled(true), 6000);
    return () => clearTimeout(t);
  }, []);

  /** "Back to tickets": give back the seats picked in this visit (not ones the cart already owns) and undo auto-added tickets. */
  const clearSelection = useCallback(async () => {
    const ids = selectedRef.current.filter((s) => !s.restored).map((s) => s.seatId);
    const auto = autoAddedRef.current;
    const reverted = { ...budgetRef.current };
    Object.entries(auto).forEach(([t, n]) => {
      reverted[t] = Math.max(0, (reverted[t] ?? 0) - n);
    });
    autoAddedRef.current = {};
    setSelected([]);
    setAssignment({});
    setExpiresAt(null);
    setBudget(reverted);
    ids.forEach((id) => releasingRef.current.add(id));
    await releaseOnServer(ids);
    setTimeout(() => ids.forEach((id) => releasingRef.current.delete(id)), 2000);
  }, [releaseOnServer, setBudget, setSelected]);

  /** Call right after the cart items were created: the cart now owns the holds. */
  const commit = useCallback(() => {
    committedRef.current = true;
  }, []);

  /**
   * Call AFTER the cart operation (updateCartItem / createCartItems) succeeded: gives back cart seats the
   * buyer dropped. Harmless if the server already released them (updateCartItem does for dropped seats).
   */
  const releaseDeferred = useCallback(async () => {
    const ids = [...deferredRef.current].filter((id) => !selectedRef.current.some((s) => s.seatId === id));
    deferredRef.current = new Set();
    setDeferredIds(new Set());
    await releaseOnServer(ids);
  }, [releaseOnServer]);

  // leaving the screen without handing the seats to the cart -> give them back now
  useEffect(
    () => () => {
      const toRelease = selectedRef.current.filter((s) => !s.restored).map((s) => s.seatId);
      if (!committedRef.current && toRelease.length && holdTokenRef.current) {
        releaseRef
          .current({ variables: { input: { chartEventId, seatIds: toRelease, holdToken: holdTokenRef.current } } })
          .catch(() => { });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartEventId],
  );

  const total = sumBudget(budget);
  const matching = useMemo(() => assignTicketTypes(selected, budget, eligibility), [selected, budget, eligibility]);
  const inventoryRows = useMemo(
    () => buildInventoryRows({ selected, assignment: matching.assignment, budget, ticketTypesById }),
    [selected, matching, budget, ticketTypesById],
  );
  const totalPrice = inventoryRows.reduce((sum, r) => sum + (ticketTypesById[r.ticketTypeId]?.price ?? 0), 0);
  const unassignedCount = inventoryRows.filter((r) => r.kind === "unassigned").length;
  const isComplete =
    total > 0 &&
    selected.length === total &&
    pending.size === 0 &&
    matching.ok;

  const getCartItems = useCallback(
    () =>
      groupIntoCartItems({
        selected: selectedRef.current,
        assignment: assignTicketTypes(selectedRef.current, budgetRef.current, eligibility).assignment,
        ticketTypesById,
        holdToken: holdTokenRef.current,
      }),
    [eligibility, ticketTypesById],
  );

  return {
    eligibility,
    ticketTypesById,
    statusById,
    statusesLoaded,
    deferredIds,
    releaseDeferred,
    selected,
    selectedIds: useMemo(() => new Set(selected.map((s) => s.seatId)), [selected]),
    assignment,
    pending,
    budget,
    totalTickets: total,
    inventoryRows,
    totalPrice,
    unassignedCount,
    removeTicket,
    suggestSeats: suggestSeatsNow,
    isComplete,
    secondsLeft,
    chooser,
    toggleSeat,
    chooseTicketType,
    cancelChooser: () => setChooser(null),
    clearSelection,
    commit,
    getCartItems,
    getHoldToken: () => holdTokenRef.current,
    inventoryReady: statusesLoaded && suggestSettled,
  };
}